// <copyright company="Microsoft">Copyright (c) Microsoft. All rights reserved.</copyright>

// France / EDICOM constants mirrored from the backend so the Manual Invoice form
// produces payloads that pass RequestGateway + EdicomFranceValidator checks.
//
// DRIFT PROTECTION: the backend values these mirror are pinned by the tripwire test
// test/Test.EInvoicing.Common/Constants/ManualInvoiceFrontendParityTests.cs — if the backend
// legal entities, VAT rates, or territory codes change, that test fails and the dev is reminded
// to update this file. Each mirrored block below names its authoritative backend source.
// Sources:
//  - EInvoicing.Common/Constants/CountryCodes.cs (FranceTerritoryCountryCodes)
//  - EInvoicing.Common/Constants/FranceConstants.cs (ValidVatRates, FrenchEntityCompanyCodes)
//  - EInvoicing.Common/Suppliers/Edicom/EdicomConstants.cs (FranceLegalEntities.ByCompanyCode)

export interface FranceLegalEntity {
    companyCode: string;
    legalName: string;
    siren: string;
    vatId: string;
}

// Reporting regimes selectable in the Manual Invoice form. Today only France
// (EDICOM) is wired end-to-end; additional countries are placeholders so the
// selector is forward-compatible. Set `enabled: true` once a country's field
// set, validation and payload mapping are implemented.
export interface ManualInvoiceCountry {
    code: string;
    label: string;
    enabled: boolean;
}

export const MANUAL_INVOICE_COUNTRIES: ManualInvoiceCountry[] = [
    { code: 'FR', label: 'France (EDICOM)', enabled: true },
    { code: '__soon', label: 'More countries coming soon', enabled: false },
];

// France + French territories + Monaco accepted by EdicomFranceValidator.
// Backend source of truth: CountryCodes.FranceTerritoryCountryCodes (pinned by ManualInvoiceFrontendParityTests).
export const FRANCE_TERRITORY_COUNTRY_CODES: { code: string; name: string }[] = [
    { code: 'FR', name: 'France' },
    { code: 'GF', name: 'French Guiana' },
    { code: 'YT', name: 'Mayotte' },
    { code: 'MF', name: 'Saint Martin' },
    { code: 'BL', name: 'Saint Barthélemy' },
    { code: 'PM', name: 'Saint Pierre and Miquelon' },
    { code: 'NC', name: 'New Caledonia' },
    { code: 'WF', name: 'Wallis and Futuna' },
    { code: 'PF', name: 'French Polynesia' },
    { code: 'CP', name: 'Clipperton' },
    { code: 'TF', name: 'French Southern & Antarctic Land' },
    { code: 'MC', name: 'Monaco' },
];

export const FRANCE_TERRITORY_CODE_SET: ReadonlySet<string> = new Set(
    FRANCE_TERRITORY_COUNTRY_CODES.map((c) => c.code)
);

// Microsoft France legal entities registered in EdicomConstants.FranceLegalEntities.ByCompanyCode.
// companyCode must be one of these keys or the validator rejects the document.
// Backend source of truth: EdicomConstants.FranceLegalEntities.ByCompanyCode (pinned by ManualInvoiceFrontendParityTests).
export const FRANCE_LEGAL_ENTITIES: FranceLegalEntity[] = [
    { companyCode: '1073', legalName: 'Microsoft France SAS', siren: '327733184', vatId: 'FR95327733184' },
    { companyCode: '1081', legalName: 'Microsoft EMEA SAS', siren: '352568091', vatId: 'FR86352568091' },
    { companyCode: '1780', legalName: 'Microsoft Engineering Center - Paris', siren: '429600158', vatId: 'FR92429600158' },
    { companyCode: '1672', legalName: 'Microsoft R&D France SAS', siren: '431920594', vatId: 'FR01431920594' },
    { companyCode: '1062', legalName: 'Microsoft Ireland Operations Ltd', siren: '419423728', vatId: 'FR00419423728' },
    { companyCode: '1985', legalName: 'Microsoft 1985 France S.a r.l.', siren: '534969647', vatId: 'FR12534969647' },
    { companyCode: '1311', legalName: 'LinkedIn France SAS', siren: '519059281', vatId: 'FR92519059281' },
    { companyCode: '2421', legalName: 'GitHub France SAS', siren: '985180215', vatId: 'FR30985180215' },
    { companyCode: '1289', legalName: 'Affirmed Networks France SARL', siren: '751604026', vatId: 'FR45751604026' },
    { companyCode: '2256', legalName: 'Arkane Studios S.A.S. (ZeniMax)', siren: '424606176', vatId: 'FR87424606176' },
    { companyCode: '2453', legalName: 'Alenty SAS', siren: '493981443', vatId: 'FR69493981443' },
    { companyCode: '2454', legalName: 'AppNexus France SARL', siren: '751969577', vatId: 'FR16751969577' },
    { companyCode: '2578', legalName: 'Blizzard France SAS', siren: '489952457', vatId: 'FR87489952457' },
    { companyCode: '2584', legalName: 'Activision Blizzard France SAS', siren: '400299566', vatId: 'FR07400299566' },
];

export const FRANCE_LEGAL_ENTITY_BY_CODE: ReadonlyMap<string, FranceLegalEntity> = new Map(
    FRANCE_LEGAL_ENTITIES.map((e) => [e.companyCode, e])
);

// Valid French VAT rates per FranceConstants.ValidVatRates (Rule G1.24 / TT-93).
// Backend source of truth: FranceConstants.ValidVatRates (pinned by ManualInvoiceFrontendParityTests).
export const VALID_FRENCH_VAT_RATES: number[] = [
    0, 0.9, 1.05, 1.75, 2.1, 5.5, 8.5, 9.2, 9.6, 10, 13, 19.6, 20, 20.6,
];

export const VALID_FRENCH_VAT_RATE_SET: ReadonlySet<number> = new Set(VALID_FRENCH_VAT_RATES);

// Document types supported by EdicomFranceValidator (serialized enum values).
export type FranceDocumentType = 'invoice' | 'creditNote' | 'paymentReceipt';

export const FRANCE_DOCUMENT_TYPES: { value: FranceDocumentType; label: string }[] = [
    { value: 'invoice', label: 'Invoice' },
    { value: 'creditNote', label: 'Credit Note' },
    { value: 'paymentReceipt', label: 'Payment Receipt' },
];

// Address types (serialized enum values). Buyer address priority is billTo -> shipTo -> soldTo.
export type FranceAddressType = 'billTo' | 'shipTo' | 'soldTo';

export const FRANCE_ADDRESS_TYPES: { value: FranceAddressType; label: string }[] = [
    { value: 'billTo', label: 'Bill To' },
    { value: 'shipTo', label: 'Ship To' },
    { value: 'soldTo', label: 'Sold To' },
];

// France-specific supply types (BT-23 sub-process). Transformer defaults to B2B when empty.
export const SUPPLY_TYPES: string[] = ['B2B', 'B2C', 'B2G'];

// Tax id dictionary keys. Backend parses these via Enum.Parse<TaxIdType>(key, ignoreCase:true),
// so the keys must be the enum NAMES (not the lowercase EnumMember values).
export const TAX_ID_KEYS = {
    VATID: 'VATID',
    SIREN: 'SIREN',
    SIRET: 'SIRET',
    ROUTINGID: 'RoutingID',
} as const;

export const REQUIRED_CURRENCY = 'EUR';
export const DEFAULT_COUNTRY_CODE = 'FR';
export const DEFAULT_TAX_TYPE = 'VAT';

export const SIREN_PATTERN = /^\d{9}$/;
export const SIRET_PATTERN = /^\d{14}$/;

// ---------------------------------------------------------------------------
// TD17 (accounts-payable / PurchaseReport) scenario constants.
// TD17 = a non-French entity (seller) invoicing a French Microsoft entity (buyer).
// Routing resolves the buyer's French company code from its tax IDs.
// ---------------------------------------------------------------------------

export const INVOICE_TYPE_CODE_TD17 = 'TD17';

// French Microsoft entity company codes whose SIREN/VAT ID resolve a buyer code
// (FranceConstants.FrenchEntityCompanyCodes). MIOL 1062 is intentionally excluded.
export const FRENCH_ENTITY_COMPANY_CODES: ReadonlySet<string> = new Set([
    '1073', '1081', '1985', '1780', '1672',
]);

export const MIOL_COMPANY_CODE = '1062';

// Company codes that ARE batch-enabled for France. Choosing one of these as the
// TD17 seller would take the normal (non-AP) routing path and bypass PurchaseReport,
// so they are rejected as a TD17 seller code.
export const FRANCE_BATCH_ENABLED_COMPANY_CODES: ReadonlySet<string> = new Set([
    ...Array.from(FRENCH_ENTITY_COMPANY_CODES),
    MIOL_COMPANY_CODE,
]);

// Note on DOM territories (Martinique MQ, Guadeloupe GP, Réunion RE): these are
// "domestic" for France e-reporting *routing*, but that distinction is derived from
// the SoldTo/buyer country on the backend (FranceConstants.DomesticCountries). They
// are NOT valid header country codes — EdicomFranceValidator requires the document
// header CountryCode to be FR or a French territory (CountryCodes.FranceTerritoryCountryCodes,
// which excludes MQ/GP/RE and expects FR for those DOMs).

// The French Microsoft entities eligible to be the TD17 buyer (the 5 resolvable ones).
export const FRENCH_ENTITY_BUYERS: FranceLegalEntity[] = FRANCE_LEGAL_ENTITIES.filter(
    (e) => FRENCH_ENTITY_COMPANY_CODES.has(e.companyCode)
);

const FRENCH_ENTITY_SIREN_SET: ReadonlySet<string> = new Set(FRENCH_ENTITY_BUYERS.map((e) => e.siren));
const FRENCH_ENTITY_VATID_SET: ReadonlySet<string> = new Set(FRENCH_ENTITY_BUYERS.map((e) => e.vatId.toUpperCase()));

// Mirrors FranceConstants.TryResolveBuyerCompanyCode: the buyer's SIREN (preferred)
// or VAT ID must exactly match one of the 5 French Microsoft entities.
export function buyerResolvesToFrenchEntity(siren: string, vatId: string): boolean {
    if (siren && FRENCH_ENTITY_SIREN_SET.has(siren.trim())) {
        return true;
    }
    if (vatId && FRENCH_ENTITY_VATID_SET.has(vatId.trim().toUpperCase())) {
        return true;
    }
    return false;
}
